import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiEye, FiEdit } from "react-icons/fi";
import EditingFloatBookingModal from "./EditingFloatBookingModal/EditingFloatBookingModal";
import EditBookingModal from "../../EditBookingModal/EditBookingModal";
import { editBookingAPI } from "../../../../utils/APIs/bookingsApis";
import { toast } from "react-toastify";
import "./UpcomingEditingBookingTable.css";
import ViewDetailsModal from "../../../../components/ViewDetailsModal/ViewDetailsModal";
import { buildEditingBookingDetailRows } from "../../../../utils/viewDetailsBuilders";
import {
  getUserCancelledBookingTitle,
  isUserCancelledBooking,
} from "../../../../utils/bookingCancelUtils";

const UpcomingEditingBookingTable = ({ data = [], page = 1, limit = 10, refetchData }) => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsBooking, setDetailsBooking] = useState(null);

  const handleGalleryViewClick = (booking) => {
    const id = booking?.bookingId || booking?._id;
    navigate(`/bookings/editing-view-gallery/${id}`);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedBooking(null);
  };

  const handleFloatBooking = (booking) => {
    setSelectedBooking(booking);
    setIsModalOpen(true);
  };

  const handleUpdateBooking = async (updatedData) => {
    try {
      const response = await editBookingAPI(selectedBooking.bookingId || selectedBooking._id, updatedData);
      if (response?.data?.success && response?.status === 200) {
        toast.success(response?.data?.message || "Booking Updated successfully!");
        if (refetchData) refetchData();
      } else {
        toast.error(response?.data?.message || "Failed to Update Booking!");
      }
      setShowEditModal(false);
    } catch (error) {
      console.error("Failed to update booking:", error);
      toast.error("An error occurred while updating.");
    } finally {
    }
  };

  const getBookingStatusText = (booking) => {
    return booking?.status || "Pending";
  };

  const formatStatus = (value) => {
    if (!value) return "Pending";
    return value
      .toString()
      .replace(/_/g, " ")
      .toLowerCase()
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <div className="editing-booking-table-wrapper">
      <table className="editing-booking-table">
        <thead>
          <tr>
            <th className="td-sr">Sr.</th>
            <th>Booking ID</th>
            <th>Client Name</th>
            <th>Date</th>
            <th>Event Type</th>
            <th>Type</th>
            <th>Price</th>
            <th>Videos Count</th>
            <th>Assigned Editor</th>
            <th>Gallery View</th>
            <th>Gallery Status</th>
            <th>Payment Status</th>
            <th>Booking Status</th>
            <th>Details</th>
            <th>Action</th>
          </tr>
        </thead>

        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan="15" style={{ textAlign: "center", padding: "20px" }}>
                No editing bookings found.
              </td>
            </tr>
          ) : (
            data.map((booking, bookingIndex) => {
              const packages =
                booking.videoPackages?.length > 0
                  ? booking.videoPackages
                  : [
                      {
                        type: "-",
                        planCategory: "-",
                        videosCount: "-",
                        assignedEditorNames: [],
                      },
                    ];
              const totalPackages = packages.length;

              return packages.map((pkg, pkgIndex) => {
                const isFirstRow = pkgIndex === 0;
                const rowKey = `${booking.bookingId}-${pkgIndex}-${pkg.type || pkg.planCategory || "plan"}`;

                return (
                  <tr
                    key={rowKey}
                    className={isFirstRow ? "editing-booking-highlight" : "editing-booking-sub-row"}
                  >
                    {isFirstRow && (
                      <>
                        <td rowSpan={totalPackages} className="td-sr">
                          {(page - 1) * limit + bookingIndex + 1}
                        </td>
                        <td rowSpan={totalPackages}>{booking.bookingId}</td>
                        <td rowSpan={totalPackages}>{booking.clientName}</td>
                        <td rowSpan={totalPackages} style={{ whiteSpace: "nowrap" }}>
                          {booking.startDate 
                            ? new Date(booking.startDate).toLocaleDateString("en-GB") 
                            : "-"}
                        </td>
                        <td rowSpan={totalPackages}>
                          {booking.eventType || booking.shootType || "-"}
                        </td>
                      </>
                    )}

                    {/* Package Level Columns (Individual rows) */}
                    <td>
                      <span
                        className={
                          (pkg.type || pkg.planCategory || "").toString().trim().toLowerCase() === "premium"
                            ? "badge-premium"
                            : "badge-standard"
                        }
                      >
                        {pkg.type || pkg.planCategory || "-"}
                      </span>
                    </td>
                    
                    {/* Price Column (Row spanned) */}
                    {isFirstRow && (
                      <td rowSpan={totalPackages}>₹ {Math.round(booking.finalAmount || booking.totalAmount || 0).toLocaleString()}</td>
                    )}

                    {/* Videos Count Column (Split per row) */}
                    <td style={{ textAlign: "center" }}>{pkg.videosCount}</td>

                    {/* Assigned Editor — assigned staff only (not pending invites) */}
                    <td>
                      {pkg.assignedEditorNames && pkg.assignedEditorNames.length > 0 ? (
                        <span style={{ fontSize: "13px", fontWeight: "600", color: "#1f2937" }}>
                          {pkg.assignedEditorNames.join(", ")}
                        </span>
                      ) : (
                        <span style={{ color: "#9ca3af", fontSize: "13px" }}>—</span>
                      )}
                    </td>

                    {/* Other Booking Level Columns (Row spanned) */}
                    {isFirstRow && (
                      <>
                        {/* Gallery View */}
                        <td rowSpan={totalPackages}>
                          <button
                            className="upload-btn d-flex align-items-center gap-2"
                            onClick={() => handleGalleryViewClick(booking)}
                            title="View Gallery"
                          >
                            <FiEye size={16} /> View
                          </button>
                        </td>

                        {/* Gallery Status */}
                        <td rowSpan={totalPackages}>
                          <span
                            className={`status ${(booking.galleryStatus || "pending").replace(/\s+/g, '')}`}
                          >
                            {booking.galleryStatus || "Pending"}
                          </span>
                        </td>

                        {/* Payment Status */}
                        <td rowSpan={totalPackages}>
                          <span className={`status ${(booking.paymentStatus || "pending").replace(/\s+/g, '')}`} style={{ textTransform: 'capitalize' }}>
                            {formatStatus(booking.paymentStatus)}
                          </span>
                        </td>

                        {/* Booking Status */}
                        <td rowSpan={totalPackages}>
                          <span className={`status ${String(getBookingStatusText(booking)).toLowerCase().replace(/\s+/g, '')}`} style={{ textTransform: 'capitalize' }}>
                            {getBookingStatusText(booking)}
                          </span>
                        </td>

                        <td rowSpan={totalPackages}>
                          <button
                            type="button"
                            className="view-details-btn"
                            onClick={() => {
                              setDetailsBooking(booking);
                              setShowDetailsModal(true);
                            }}
                          >
                            View Details
                          </button>
                        </td>

                        {/* Action Column */}
                        <td rowSpan={totalPackages}>
                          <div className="d-flex flex-row gap-2 align-items-center justify-content-center">
                            <button
                              type="button"
                              className={`upload-btn ${isUserCancelledBooking(booking) ? "disabled" : ""}`}
                              onClick={() => {
                                if (isUserCancelledBooking(booking)) return;
                                handleFloatBooking(booking);
                              }}
                              disabled={isUserCancelledBooking(booking)}
                              title={getUserCancelledBookingTitle(booking)}
                            >
                              {(booking?.assignedPhotographer && booking?.displayStatus !== "Portal Cancel") ? "View Assigned Editor" : "Float Booking"}
                            </button>
                            <button
                              type="button"
                              className={`upload-btn d-flex align-items-center gap-2 justify-content-center ${isUserCancelledBooking(booking) ? "disabled" : ""}`}
                              onClick={() => {
                                if (isUserCancelledBooking(booking)) return;
                                setSelectedBooking(booking);
                                setShowEditModal(true);
                              }}
                              disabled={isUserCancelledBooking(booking)}
                              title={getUserCancelledBookingTitle(booking, "Edit Booking")}
                            >
                              <FiEdit size={16} /> Edit
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                );
              });
            })
          )}
        </tbody>
      </table>

      {isModalOpen && (
        <EditingFloatBookingModal
          booking={selectedBooking}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          refetchData={refetchData}
        />
      )}

      {showEditModal && (
        <EditBookingModal
          booking={selectedBooking}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSave={handleUpdateBooking}
          refetchData={refetchData}
        />
      )}

      <ViewDetailsModal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title="Editing Booking Details"
        rows={buildEditingBookingDetailRows(detailsBooking)}
      />
    </div>
  );
};

export default UpcomingEditingBookingTable;
